/**
 * Phase 20 wizard — Step 4: photos (optional).
 *
 * Photos are optional and unlock the render preview. "Add a photo" opens
 * the device camera / file picker and uploads directly (reusing the shared
 * PhotoUploadButton) — no chat detour. "Skip for now" advances without
 * blocking the estimate (FR-007).
 */

import type { Photo } from "@starz/shared/api/types.gen";
import { PhotoUploadButton } from "@/components/photos/PhotoUploadButton";

interface Props {
  estimateId: string;
  photoCount: number;
  /** Called with the uploaded Photo so the wizard reflects it. */
  onUploaded: (photo: Photo) => void;
  /** Skip the optional step and advance to the estimate. */
  onSkip: () => void;
  disabled?: boolean;
}

export function PhotosStep({
  estimateId,
  photoCount,
  onUploaded,
  onSkip,
  disabled = false,
}: Props) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-warm-gray">
        A photo makes your estimate more accurate and unlocks a design preview. It's optional — you
        can add one now or skip.
      </p>

      {photoCount > 0 ? (
        <p className="rounded-lg bg-starz-cream px-4 py-3 text-sm font-medium text-starz-navy">
          {photoCount} photo{photoCount === 1 ? "" : "s"} added.
        </p>
      ) : null}

      <PhotoUploadButton
        estimateId={estimateId}
        currentCount={photoCount}
        onUploaded={onUploaded}
      />

      <button
        type="button"
        onClick={onSkip}
        disabled={disabled}
        className="min-h-[44px] rounded-lg border border-starz-gold/30 px-5 py-3 text-sm font-semibold text-warm-gray hover:bg-starz-cream disabled:opacity-40"
      >
        {photoCount > 0 ? "Continue" : "Skip for now"}
      </button>
    </div>
  );
}
